import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import dealPageService from '~/services/cruises/DealPageService';

import { arrayToParam, paramToArray, parseSearchString, stringifyQuery } from '~/utils/url';

import { CruiseDealPageBase } from '../pages/DealsPage/types';

export interface DealPageFilters {
  name?: string;
  cruiseLineId?: string;
  rateCodes?: string[];
}

interface CruisesData {
  dealsPage: Array<CruiseDealPageBase>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  filters: DealPageFilters;
  activeTab: string;
  setFilters: (filters: DealPageFilters) => void;
  setActiveTab: (tab: string) => void;
  paginate: (page: number) => void;
  getDealsPage: () => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export default function useCruiseDealsPage(): CruisesData {
  const { search } = useLocation();
  const { push: setQueryString } = useHistory();
  const [total, setTotal] = useState(0);
  const [dealsPage, setDealsPage] = useState<Array<CruiseDealPageBase>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const queryParams = useMemo(() => parseSearchString(search), [search]);

  const page = parseInt(queryParams.page as string) || 1;
  const activeTab = (queryParams.activeTab as string) || 'live';

  const filters: DealPageFilters = useMemo(
    () => ({
      name: (queryParams.name as string) || undefined,
      cruiseLineId: (queryParams.cruiseLineId as string) || undefined,
      rateCodes: paramToArray(queryParams.rateCodes as string),
    }),
    [queryParams.name, queryParams.cruiseLineId, queryParams.rateCodes],
  );

  const currentUrlParams = useCallback(
    (overrides: Record<string, string | undefined>) =>
      stringifyQuery({
        page: page.toString(),
        activeTab: activeTab.toString(),
        name: filters.name,
        cruiseLineId: filters.cruiseLineId,
        rateCodes: arrayToParam(filters.rateCodes),
        ...overrides,
      }),
    [page, activeTab, filters],
  );

  const setFilters = useCallback(
    (newFilters: DealPageFilters) => {
      setQueryString({
        search: currentUrlParams({
          page: '1',
          name: newFilters.name,
          cruiseLineId: newFilters.cruiseLineId,
          rateCodes: arrayToParam(newFilters.rateCodes),
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setQueryString({
        search: currentUrlParams({
          page: '1',
          activeTab: tab,
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const paginate = useCallback(
    (page: number) => {
      setQueryString({
        search: currentUrlParams({
          page: page.toString(),
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const getDealsPage = useCallback(async () => {
    try {
      const skip = (page - 1) * RECORDS_PER_PAGE;
      const isActive = activeTab === 'live';
      setIsLoading(true);
      const res = await dealPageService.listWithPagination({
        skip,
        take: RECORDS_PER_PAGE,
        isActive,
        ...filters,
      });
      setDealsPage(res.result);
      setTotal(res.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [page, activeTab, filters]);

  useEffect(() => {
    getDealsPage();
  }, [getDealsPage]);

  return {
    dealsPage,
    getDealsPage,
    page,
    recordsPerPage: RECORDS_PER_PAGE,
    filters,
    activeTab,
    setFilters,
    setActiveTab,
    paginate,
    total,
    isLoading,
  };
}
